/**
 * Goal Create Modal Component
 *
 * Modal for creating new goals with title, description, type, and target date.
 */

import { useState } from 'react';
import { Button } from '@/components/ui';
import type { CreateGoalInput, GoalType } from '@/types';

interface GoalCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateGoalInput) => void;
  isCreating?: boolean;
}

export function GoalCreateModal({
  isOpen,
  onClose,
  onCreate,
  isCreating = false,
}: GoalCreateModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [goalType, setGoalType] = useState<GoalType>('outcome');
  const [targetDate, setTargetDate] = useState('');
  const [keyResults, setKeyResults] = useState<string[]>(['']);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const input: CreateGoalInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      goalType,
      targetDate: targetDate || undefined,
      keyResults: keyResults.filter(kr => kr.trim()).length > 0
        ? keyResults.filter(kr => kr.trim())
        : undefined,
    };

    onCreate(input);
    
    // Reset form
    setTitle('');
    setDescription('');
    setGoalType('outcome');
    setTargetDate('');
    setKeyResults(['']);
  };

  const handleAddKeyResult = () => {
    setKeyResults([...keyResults, '']);
  };

  const handleKeyResultChange = (index: number, value: string) => {
    const newKeyResults = [...keyResults];
    newKeyResults[index] = value;
    setKeyResults(newKeyResults);
  };

  const handleRemoveKeyResult = (index: number) => {
    if (keyResults.length > 1) {
      setKeyResults(keyResults.filter((_, i) => i !== index));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl" data-testid="goal-create-modal">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900" data-testid="goal-create-modal-title">Create Goal</h2>
            <button
              data-testid="close-button"
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What do you want to achieve?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Why is this goal important? What will success look like?"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            {/* Goal Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'outcome', label: 'Outcome', icon: '🎯', desc: 'Specific result' },
                  { value: 'process', label: 'Process', icon: '🔄', desc: 'Ongoing practice' },
                  { value: 'milestone', label: 'Milestone', icon: '🏁', desc: 'Checkpoint' },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setGoalType(type.value as GoalType)}
                    className={`
                      p-3 rounded-lg border-2 text-center transition-colors
                      ${goalType === type.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <div className="text-xl mb-1">{type.icon}</div>
                    <div className="text-sm font-medium">{type.label}</div>
                    <div className="text-xs text-gray-500">{type.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Target Date */}
            <div>
              <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700 mb-1">
                Target Date
              </label>
              <input
                id="targetDate"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Key Results */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Key Results (Optional)
                </label>
                <button
                  type="button"
                  onClick={handleAddKeyResult}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {keyResults.map((kr, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={kr}
                      onChange={(e) => handleKeyResultChange(index, e.target.value)}
                      placeholder={`Key result ${index + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {keyResults.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyResult(index)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Define measurable results that indicate success
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!title.trim() || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Goal'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

